import { useCallback, useState, useMemo, useEffect } from "react";
import BaseTemplate from "components/templates/base-template";
import BaseHeaderComponent from "segments/desktop/header/layouts/base-header/base-header-component";
import LongFooterComponent from "segments/desktop/footer/layouts/long-footer/long-footer-component";
import TemplatesNavigation from "template-store/components/navigation/navigation-component";
import MainPageContent from "template-store/components/main-page-content/main-page-content";
import { filterSolutions, getSolution } from "template-store/services/solutions-service";
import { HOME_ID } from "template-store/constants/categories";
import { getSolutionName, getSolutionEnglishName } from "template-store/services/solution-data-service";
import { getSolutionPageLink, getSolutionSearchLink } from "template-store/services/solution-links-service";

import { SOLUTION_STORE_SEARCH } from "constants/bigbrain-event-types";
import { trackEvent } from "services/bigbrain-service";

import {
  SOLUTIONS_IN_CATEGORY_PAGE_TYPE,
  SOLUTIONS_HOME_PAGE_TYPE,
  SINGLE_SOLUTION_PAGE_TYPE
} from "constants/template-center/solution-page-types";

import { componentStyles } from "./solution-page-layout-component.scss";

const SEARCH_TERM_MIN_LENGTH_FOR_BIGBRAIN_EVENT = 3;

const SolutionPageLayoutComponent = (props) => {
  const {
    pageType,
    allSolutions,
    allCategories,
    categoryId,
    solutionId,
    searchTermFromAdressBar,
    tabsMetadata,
    ...baseTemplateProps
  } = props;
  const [searchTerm, setsearchTerm] = useState("");
  const [filteredSolutions, setFilteredSolutions] = useState([]);

  const handleSearchTermChange = useCallback(
    (term) => {
      const solutions = filterSolutions(allSolutions, term);
      setsearchTerm(term);
      setFilteredSolutions(solutions);

      if (term.length >= SEARCH_TERM_MIN_LENGTH_FOR_BIGBRAIN_EVENT)
        trackEvent(SOLUTION_STORE_SEARCH, { kind: term, info1: solutions?.length });

      history.replaceState({}, "", getSolutionSearchLink(term));
    },
    [allSolutions]
  );

  useEffect(() => {
    if (searchTermFromAdressBar) {
      handleSearchTermChange(searchTermFromAdressBar);
    }
  }, []);

  const selectedCategoryId = useMemo(() => {
    if (pageType === SOLUTIONS_IN_CATEGORY_PAGE_TYPE) return categoryId;
    if (pageType === SOLUTIONS_HOME_PAGE_TYPE) return HOME_ID;
    return null;
  }, [categoryId, pageType]);

  const pageMetaTitle = useMemo(() => {
    switch (pageType) {
      case SOLUTIONS_IN_CATEGORY_PAGE_TYPE:
        return `templateCenter.${categoryId}.meta.title`;
      case SINGLE_SOLUTION_PAGE_TYPE:
        const solution = getSolution(allSolutions, solutionId);
        const solutionName = getSolutionName(solution);
        return { key: "templateCenter.specificTemplate.meta.title", param: solutionName };
      default:
        return "templateCenter.meta.title";
    }
  }, [categoryId, solutionId, pageType, allSolutions]);

  const canonicalUrl = useMemo(() => {
    if (pageType !== SINGLE_SOLUTION_PAGE_TYPE) return null;

    const solution = getSolution(allSolutions, solutionId);
    const solutionEnglishName = getSolutionEnglishName(solution);
    return getSolutionPageLink(solutionId, solutionEnglishName);
  }, [allSolutions, pageType, solutionId]);

  return (
    <BaseTemplate
      {...baseTemplateProps}
      title={pageMetaTitle}
      description="templateCenter.meta.description"
      enableHotjar="true"
      canonicalUrl={canonicalUrl}
    >
      <div className="solution-page-component">
        <BaseHeaderComponent />
        <div className="template-page-content">
          <nav className="desktop-navigation-wrapper" aria-label="Template categories">
            <div className="navigation-sticky-wrapper">
              <TemplatesNavigation
                onSearchChange={handleSearchTermChange}
                searchTerm={searchTerm}
                allCategories={allCategories}
                selectedCategoryId={selectedCategoryId}
              />
            </div>
          </nav>

          <main className="page-main-content">
            <MainPageContent
              pageType={pageType}
              allSolutions={allSolutions}
              allCategories={allCategories}
              categoryId={categoryId}
              solutionId={solutionId}
              searchTerm={searchTerm}
              filteredSolutions={filteredSolutions}
              tabsMetadata={tabsMetadata}
              selectedCategoryId={selectedCategoryId}
              onSearchChange={handleSearchTermChange}
            />
          </main>
        </div>
        <LongFooterComponent />

        <style jsx>{componentStyles}</style>
      </div>
    </BaseTemplate>
  );
};

export default SolutionPageLayoutComponent;
